/**
 * Trade Image Capture Service (KAN-82)
 * Frontend API client for extract + config endpoints
 */

import { apiClient, APIResponse } from './client';
import {
  TradeImageCaptureResult,
  TradeImageCaptureConfig,
  SourceAnalysisResult,
  MultiTradeImageCaptureConfig,
  MultiTradeExtractionResult,
  AllocationExtractionResult,
} from '../../types/tradeImageCapture';

class TradeImageCaptureService {
  /**
   * Extract trade data from a pasted screenshot.
   */
  async extractFromImage(
    clientId: string,
    imageBase64: string,
    imageMimeType: string,
    imageFileSizeBytes: number
  ): Promise<TradeImageCaptureResult> {
    const response: APIResponse<TradeImageCaptureResult> = await apiClient.post(
      '/api/v1/trade-image-capture/extract',
      {
        clientId,
        imageBase64,
        imageMimeType,
        imageFileSizeBytes,
      }
    );
    return response.data;
  }

  /**
   * Get trade image capture config for a client.
   */
  async getConfig(clientId: string): Promise<TradeImageCaptureConfig | null> {
    try {
      const response: APIResponse<TradeImageCaptureConfig> = await apiClient.get(
        `/api/v1/trade-image-capture/config/${clientId}`
      );
      return response.data;
    } catch (error: any) {
      // apiClient throws plain Error with message containing status code
      if (error.message?.includes('404')) return null;
      throw error;
    }
  }

  /**
   * Save trade image capture config for a client.
   * Optionally includes source image base64 for GCS upload.
   */
  async saveConfig(
    clientId: string,
    config: TradeImageCaptureConfig,
    imageBase64?: string,
    imageMimeType?: string,
  ): Promise<void> {
    const payload: any = { ...config };
    if (imageBase64 && imageMimeType) {
      payload.imageBase64 = imageBase64;
      payload.imageMimeType = imageMimeType;
    }
    await apiClient.put(`/api/v1/trade-image-capture/config/${clientId}`, payload);
  }

  /**
   * Delete trade image capture config and source image for a client.
   */
  async deleteConfig(clientId: string): Promise<void> {
    await apiClient.delete(`/api/v1/trade-image-capture/config/${clientId}`);
  }

  /**
   * Get a signed URL for the stored source system screenshot.
   */
  async getSourceImageUrl(clientId: string): Promise<string | null> {
    try {
      const response: APIResponse<{ signedUrl: string }> = await apiClient.get(
        `/api/v1/trade-image-capture/source-image/${clientId}`
      );
      return response.data.signedUrl;
    } catch (error: any) {
      if (error.message?.includes('404')) return null;
      throw error;
    }
  }

  /**
   * Analyse a source system screenshot to propose field mappings.
   */
  async analyzeSource(
    clientId: string,
    imageBase64: string,
    imageMimeType: string,
    imageFileSizeBytes: number
  ): Promise<SourceAnalysisResult> {
    const response: APIResponse<SourceAnalysisResult> = await apiClient.post(
      '/api/v1/trade-image-capture/analyze-source',
      { clientId, imageBase64, imageMimeType, imageFileSizeBytes }
    );
    return response.data;
  }
  // --- KAN-125: Multi-Trade Methods ---

  /**
   * Get multi-trade config for a client.
   */
  async getMultiConfig(clientId: string): Promise<MultiTradeImageCaptureConfig | null> {
    try {
      const response: APIResponse<MultiTradeImageCaptureConfig> = await apiClient.get(
        `/api/v1/trade-image-capture/multi-config/${clientId}`
      );
      return response.data;
    } catch (error: any) {
      if (error.message?.includes('404')) return null;
      throw error;
    }
  }

  /**
   * Save multi-trade config for a client.
   * Supports dual image upload (multi-trade source + allocation source).
   */
  async saveMultiConfig(
    clientId: string,
    config: MultiTradeImageCaptureConfig,
    imageBase64?: string,
    imageMimeType?: string,
    allocationImageBase64?: string,
    allocationImageMimeType?: string,
  ): Promise<void> {
    const payload: any = { ...config };
    if (imageBase64 && imageMimeType) {
      payload.multiSourceImageBase64 = imageBase64;
      payload.multiSourceImageMimeType = imageMimeType;
    }
    if (allocationImageBase64 && allocationImageMimeType) {
      payload.allocationSourceImageBase64 = allocationImageBase64;
      payload.allocationSourceImageMimeType = allocationImageMimeType;
    }
    await apiClient.put(`/api/v1/trade-image-capture/multi-config/${clientId}`, payload);
  }

  /**
   * Delete multi-trade config and associated images for a client.
   */
  async deleteMultiConfig(clientId: string): Promise<void> {
    await apiClient.delete(`/api/v1/trade-image-capture/multi-config/${clientId}`);
  }

  /**
   * Get signed URL for stored multi-trade source screenshot.
   */
  async getMultiSourceImageUrl(clientId: string): Promise<string | null> {
    try {
      const response: APIResponse<{ signedUrl: string }> = await apiClient.get(
        `/api/v1/trade-image-capture/multi-source-image/${clientId}`
      );
      return response.data.signedUrl;
    } catch (error: any) {
      if (error.message?.includes('404')) return null;
      throw error;
    }
  }

  /**
   * Get signed URL for stored allocation source screenshot.
   */
  async getAllocationSourceImageUrl(clientId: string): Promise<string | null> {
    try {
      const response: APIResponse<{ signedUrl: string }> = await apiClient.get(
        `/api/v1/trade-image-capture/allocation-source-image/${clientId}`
      );
      return response.data.signedUrl;
    } catch (error: any) {
      if (error.message?.includes('404')) return null;
      throw error;
    }
  }

  /**
   * Analyse a multi-trade table screenshot to propose column mappings.
   */
  async analyzeMultiSource(
    clientId: string,
    imageBase64: string,
    imageMimeType: string,
    imageFileSizeBytes: number,
  ): Promise<SourceAnalysisResult> {
    const response: APIResponse<SourceAnalysisResult> = await apiClient.post(
      '/api/v1/trade-image-capture/analyze-multi-source',
      { clientId, imageBase64, imageMimeType, imageFileSizeBytes }
    );
    return response.data;
  }

  /**
   * Analyse an allocation matrix screenshot for config setup.
   */
  async analyzeAllocationSource(
    clientId: string,
    imageBase64: string,
    imageMimeType: string,
    imageFileSizeBytes: number,
  ): Promise<any> {
    const response: APIResponse<any> = await apiClient.post(
      '/api/v1/trade-image-capture/analyze-allocation-source',
      { clientId, imageBase64, imageMimeType, imageFileSizeBytes }
    );
    return response.data;
  }

  /**
   * Extract multiple trades from a table screenshot.
   */
  async extractMultiTrade(
    clientId: string,
    imageBase64: string,
    imageMimeType: string,
    imageFileSizeBytes: number,
  ): Promise<MultiTradeExtractionResult> {
    const response: APIResponse<MultiTradeExtractionResult> = await apiClient.post(
      '/api/v1/trade-image-capture/extract-multi',
      { clientId, imageBase64, imageMimeType, imageFileSizeBytes }
    );
    return response.data;
  }

  /**
   * Extract allocations from a matrix screenshot.
   */
  async extractAllocations(
    clientId: string,
    imageBase64: string,
    imageMimeType: string,
    imageFileSizeBytes: number,
  ): Promise<AllocationExtractionResult> {
    const response: APIResponse<AllocationExtractionResult> = await apiClient.post(
      '/api/v1/trade-image-capture/extract-allocations',
      { clientId, imageBase64, imageMimeType, imageFileSizeBytes }
    );
    return response.data;
  }
}

export const tradeImageCaptureService = new TradeImageCaptureService();
